import React, { ReactNode, useState } from 'react';

import { Box } from '@vk-hr-tek/ui/Box';
import { Grid } from '@vk-hr-tek/ui/Grid';
import { InfoBox } from '@vk-hr-tek/ui/InfoBox';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { FileInput, FileInputValue } from '@vk-hr-tek/ui/input';

import { composeValidators, ValidateType } from '../validation';

/**
 * Структура ошибки загрузки CSV.
 */
interface CsvUploadErrorShape {
  errorDetails?: string;
  info?: unknown;
}

/**
 * Свойства компонента {@link CsvInput}
 */
interface CsvInputProps {
  /**
   * Загрузка файла. Возвращает массив id или бросает ошибку.
   */
  onUpload: (file: File) => Promise<string[]>;

  /**
   * Обработчик успешной загрузки.
   */
  onUploaded: (ids: string[]) => void;

  /**
   * Обработчик удаления файла.
   */
  onCleared: () => void;

  /**
   * Подпись поля.
   */
  label: string;

  /**
   * Текст заполнителя.
   */
  placeholder?: string;

  /**
   * Всплывающая подсказка.
   */
  tooltip?: ReactNode;

  /**
   * Массив валидаторов.
   */
  validate?: (ValidateType | undefined)[];

  /**
   * Заголовок блока с ошибками. По умолчанию `Ошибки в файле (N)`.
   */
  errorBoxTitle?: (count: number) => string;

  /**
   * Текст при множественных ошибках в файле.
   */
  multipleErrorsText?: string;

  /**
   * Занимать всю ширину контейнера.
   */
  fullWidth?: boolean;

  /**
   * Недоступность поля.
   */
  disabled?: boolean;
}

const DEFAULT_MULTIPLE_ERRORS_TEXT =
  'Некорректные данные. Исправьте ошибку в файле и загрузите повторно';

const parseUploadError = (
  error: CsvUploadErrorShape,
  multipleErrorsText: string,
): { parsedErrorText: string; parsedErrors: string[] } => {
  const errorDetails = error.errorDetails;

  if (errorDetails) {
    const parts = errorDetails
      .split('\n')
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length > 1) {
      return {
        parsedErrorText: multipleErrorsText,
        parsedErrors: parts,
      };
    }

    if (parts.length === 1) {
      return { parsedErrorText: parts[0], parsedErrors: [] };
    }
  }

  const info = error.info;
  return {
    parsedErrorText: typeof info === 'string' ? info : '',
    parsedErrors: [],
  };
};

/**
 * Компонент для загрузки CSV-файла с обработкой ошибок.
 *
 * @public
 * @param onUpload - Загрузка файла.
 * @param onUploaded - Обработчик успешной загрузки.
 * @param onCleared - Обработчик удаления файла.
 * @param label - Подпись поля.
 * @param placeholder - Текст заполнителя.
 * @param tooltip - Всплывающая подсказка.
 * @param validate - Массив валидаторов.
 * @param errorBoxTitle - Заголовок блока с ошибками.
 * @param multipleErrorsText - Текст при множественных ошибках в файле.
 * @param fullWidth - Занимать всю ширину контейнера.
 * @param disabled - Недоступность поля.
 * @returns Компонент для загрузки CSV-файла.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link CsvInputProps}
 */
export const CsvInput = ({
  onUpload,
  onUploaded,
  onCleared,
  label,
  placeholder,
  tooltip,
  validate,
  errorBoxTitle = (count) => `Ошибки в файле (${count})`,
  multipleErrorsText = DEFAULT_MULTIPLE_ERRORS_TEXT,
  fullWidth,
  disabled,
}: CsvInputProps) => {
  const [fileValue, setFileValue] = useState<FileInputValue | undefined>();
  const [errors, setErrors] = useState<string[]>([]);
  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);

  const validators = composeValidators(validate || []);

  const resetErrors = () => {
    setErrors([]);
    setErrorText('');
  };

  const handleChange = async (file?: FileInputValue) => {
    resetErrors();
    setFileValue(file);

    if (!file) {
      onCleared();
      return;
    }

    setLoading(true);

    try {
      const uploadedIds = await onUpload(file.value as File);
      onUploaded(uploadedIds);
      setErrorText('');
    } catch (uploadError) {
      const { parsedErrorText, parsedErrors } = parseUploadError(
        uploadError as CsvUploadErrorShape,
        multipleErrorsText,
      );

      setErrors(parsedErrors);
      setErrorText(parsedErrorText);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing="24">
      <Grid item xs={12} md={9}>
        <Box mb="-16">
          <FileInput
            label={label}
            required
            loading={loading}
            value={fileValue}
            error={!!errorText}
            errorText={errorText}
            onChange={handleChange}
            placeholder={placeholder}
            fullWidth={fullWidth}
            disabled={disabled}
            validators={validators}
            tooltip={tooltip}
          />
        </Box>
      </Grid>

      {errors.length > 0 && (
        <Grid item xs={12} md={6}>
          <InfoBox title={errorBoxTitle(errors.length)} showIcon>
            <Box display="flex" flexDirection="column" rowGap={1}>
              {errors.map((item) => (
                <Typography
                  key={item}
                  variant="body3"
                  color="text.light.tertirary"
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </InfoBox>
        </Grid>
      )}
    </Grid>
  );
};
